import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Settings, Moon, Sun, Shield, Bell, User, Lock, Eye, EyeOff, Loader2, Smartphone, LogOut as LogOutIcon, Monitor, Linkedin } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SettingsPage = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [isPasswordOpen, setIsPasswordOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [savingLinkedin, setSavingLinkedin] = useState(false);

  const fetchCompanySettings = React.useCallback(async () => {
    if (!isAdminHR) return;
    try {
      const r = await axios.get(`${API_URL}/api/company-settings`, { withCredentials: true });
      setLinkedinUrl(r.data?.linkedin_url || '');
    } catch {
      /* non-blocking */
    }
  }, [isAdminHR]);

  React.useEffect(() => { fetchCompanySettings(); }, [fetchCompanySettings]);

  const handleSaveLinkedin = async () => {
    setSavingLinkedin(true);
    try {
      const r = await axios.put(`${API_URL}/api/company-settings`, { linkedin_url: linkedinUrl }, { withCredentials: true });
      setLinkedinUrl(r.data?.linkedin_url || '');
      toast.success('Company LinkedIn link updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update LinkedIn link');
    } finally { setSavingLinkedin(false); }
  };

  const fetchSessions = React.useCallback(async () => {
    setSessionsLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/auth/sessions`, { withCredentials: true });
      setSessions(r.data.sessions || []);
    } catch {
      toast.error('Failed to load login history');
    } finally { setSessionsLoading(false); }
  }, []);

  React.useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const handleLogoutAll = async () => {
    if (!window.confirm('Log out of every device including this one? You will need to sign in again.')) return;
    setLoggingOutAll(true);
    try {
      await axios.post(`${API_URL}/api/auth/logout-all`, {}, { withCredentials: true });
      toast.success('Logged out of all devices');
      window.location.href = '/login';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
      setLoggingOutAll(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password) {
      toast.error('Please fill in all fields');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('New password and confirm password do not match');
      return;
    }
    if (passwordForm.current_password === passwordForm.new_password) {
      toast.error('New password must be different from current password');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/auth/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setIsPasswordOpen(false);
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Settings className="w-7 h-7 text-primary" />
          Settings
        </h1>
        <p className="text-muted-foreground">Manage your account preferences</p>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile
          </CardTitle>
          <CardDescription>Your account information</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-muted-foreground">Name</Label>
              <p className="font-medium">{user?.name}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Email</Label>
              <p className="font-medium">{user?.email}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Role</Label>
              <p className="font-medium capitalize">{user?.role}</p>
            </div>
            <div>
              <Label className="text-muted-foreground">Department</Label>
              <p className="font-medium">{user?.department || 'Not assigned'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Company Settings (Admin/HR only) */}
      {isAdminHR && (
        <Card data-testid="company-settings-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Linkedin className="w-5 h-5 text-[#0A66C2]" />
              Company Settings
            </CardTitle>
            <CardDescription>Controls the LinkedIn link shown on the employee dashboard</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="linkedin-url">Company LinkedIn Page URL</Label>
              <Input
                id="linkedin-url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://www.linkedin.com/company/your-company/"
                data-testid="company-linkedin-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSaveLinkedin} disabled={savingLinkedin} data-testid="save-linkedin-btn">
                {savingLinkedin && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save
              </Button>
              {linkedinUrl && (
                <Button variant="outline" onClick={() => window.open(linkedinUrl, '_blank')} data-testid="preview-linkedin-btn">
                  <Linkedin className="w-4 h-4 mr-2" /> Open Link
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            Appearance
          </CardTitle>
          <CardDescription>Customize how the app looks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">Toggle between light and dark themes</p>
            </div>
            <Switch
              checked={theme === 'dark'}
              onCheckedChange={toggleTheme}
              data-testid="theme-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Notifications
          </CardTitle>
          <CardDescription>Manage notification preferences</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[
            { id: 'leave', label: 'Leave Approvals', desc: 'Get notified when your leave is approved/rejected' },
            { id: 'expense', label: 'Expense Updates', desc: 'Get notified about expense claim status' },
            { id: 'okr', label: 'OKR Reminders', desc: 'Weekly reminders to update your OKR progress' },
            { id: 'announcements', label: 'Company Announcements', desc: 'Important updates from HR' },
          ].map((item) => (
            <div key={item.id} className="flex items-center justify-between">
              <div>
                <p className="font-medium">{item.label}</p>
                <p className="text-sm text-muted-foreground">{item.desc}</p>
              </div>
              <Switch defaultChecked />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Security */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security
          </CardTitle>
          <CardDescription>Account security settings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Change Password</p>
              <p className="text-sm text-muted-foreground">Update your account password</p>
            </div>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { setPasswordForm({ current_password: '', new_password: '', confirm_password: '' }); setIsPasswordOpen(true); }} data-testid="change-password-btn">
              <Lock className="w-3.5 h-3.5" /> Change
            </Button>
          </div>
          <Separator />
          <div>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-medium flex items-center gap-2"><Monitor className="w-4 h-4" /> Recent login activity</p>
                <p className="text-sm text-muted-foreground">Latest sign-ins to your account. New sessions are recorded going forward.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleLogoutAll}
                disabled={loggingOutAll || sessions.length === 0}
                data-testid="logout-all-btn"
              >
                {loggingOutAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOutIcon className="w-3.5 h-3.5" />}
                Log out from all devices
              </Button>
            </div>
            {sessionsLoading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : sessions.length === 0 ? (
              <p className="text-sm text-muted-foreground py-2">No login history yet. This is the first login being recorded.</p>
            ) : (
              <div className="border rounded-md divide-y" data-testid="login-sessions-list">
                {sessions.map((s, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3">
                    <Smartphone className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium truncate">{s.device}</p>
                        {s.is_current && (
                          <Badge variant="outline" className="text-[10px] gap-1 bg-emerald-50 dark:bg-emerald-950/30 border-emerald-300 text-emerald-700 dark:text-emerald-400">
                            Current session
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {s.ip || 'unknown ip'} · {s.at ? new Date(s.at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Separator />
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Two-Factor Authentication</p>
              <p className="text-sm text-muted-foreground">Add an extra layer of security</p>
            </div>
            <Button variant="outline" size="sm" disabled>Coming Soon</Button>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={isPasswordOpen} onOpenChange={setIsPasswordOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="w-5 h-5" /> Change Password</DialogTitle>
            <DialogDescription>Enter your current password and choose a new one</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  placeholder="Enter current password"
                  data-testid="current-password"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowCurrent(!showCurrent)}>
                  {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <div className="relative">
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  placeholder="At least 6 characters"
                  data-testid="new-password"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowNew(!showNew)}>
                  {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <div className="relative">
                <Input
                  type={showConfirm ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  placeholder="Re-enter new password"
                  data-testid="confirm-password"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowConfirm(!showConfirm)}>
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {passwordForm.new_password && passwordForm.confirm_password && passwordForm.new_password !== passwordForm.confirm_password && (
              <p className="text-xs text-destructive">Passwords do not match</p>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setIsPasswordOpen(false)}>Cancel</Button>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || passwordForm.new_password !== passwordForm.confirm_password}
                data-testid="submit-change-password"
              >
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Change Password
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* About */}
      <Card>
        <CardContent className="py-6">
          <div className="text-center">
            <p className="text-sm text-muted-foreground">Wisewit HRMS</p>
            <p className="text-xs text-muted-foreground mt-1">Version 2.0.0</p>
            <p className="text-xs text-muted-foreground mt-2">© 2025 Wisewit Solutions Pvt Ltd. All rights reserved.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SettingsPage;
